/**
 * VERA Audit Log Page
 *
 * Admin view showing all login sessions with IP, time, duration, and user-agent.
 * Fetches from /api/audit-query and displays in a sortable table.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Text,
  Card,
  Spinner,
  Badge,
  Button,
  Select,
  tokens,
} from "@fluentui/react-components";
import {
  ShieldKeyhole24Regular,
  ArrowClockwise24Regular,
  Globe24Regular,
  Clock24Regular,
  PersonCircle24Regular,
  Timer24Regular,
} from "@fluentui/react-icons";
import { fetchAuditLog, type AuditRecord } from "../services/auditService";

// ── Styles ────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "16px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #004C97 0%, #0066cc 100%)",
    color: "#ffffff",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 20px",
    minWidth: "180px",
  },
  statIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "#f0f4f8",
  },
  tableCard: {
    padding: "0",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    fontFamily: "'Open Sans', sans-serif",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: "#f5f7fa",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#63666A",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  td: {
    padding: "10px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    verticalAlign: "middle",
  },
  rowEven: {
    backgroundColor: "#fafbfc",
  },
  ipCell: {
    fontFamily: "'Consolas', 'Courier New', monospace",
    fontWeight: 500,
    color: "#004C97",
  },
  timeCell: {
    whiteSpace: "nowrap",
  },
  uaCell: {
    maxWidth: "250px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#8a8d91",
    fontSize: "12px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: "12px",
  },
});

// ── Helpers ───────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function parseUserAgent(ua: string): string {
  if (!ua) return "Unknown";
  // Extract just the browser name
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/") && !ua.includes("Chrome")) return "Safari";
  return ua.substring(0, 40) + "…";
}

function getMonthOptions(): { value: string; label: string }[] {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
}

// ── Component ─────────────────────────────────────────────────

export const AuditLogPage: React.FC = () => {
  const styles = useStyles();
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState("");
  const monthOptions = getMonthOptions();

  const loadData = useCallback(async () => {
    setLoading(true);
    const data = await fetchAuditLog(selectedMonth || undefined, 200);
    setRecords(data);
    setLoading(false);
  }, [selectedMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Stats
  const activeSessions = records.filter((r) => r.status === "active").length;
  const uniqueIps = new Set(records.map((r) => r.clientIp)).size;
  const totalSessions = records.length;

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <ShieldKeyhole24Regular />
          </div>
          <div>
            <Text weight="semibold" size={500} style={{ fontFamily: "'Poppins', sans-serif" }}>
              Access Audit Log
            </Text>
            <Text size={200} style={{ color: "#63666A", display: "block" }}>
              Login history with IP addresses and session duration
            </Text>
          </div>
        </div>

        <div className={styles.controls}>
          <Select
            value={selectedMonth}
            onChange={(_, data) => setSelectedMonth(data.value)}
          >
            <option value="">Recent (2 months)</option>
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            appearance="subtle"
            icon={<ArrowClockwise24Regular />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsRow}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <PersonCircle24Regular style={{ color: "#004C97" }} />
          </div>
          <div>
            <Text size={600} weight="semibold">{totalSessions}</Text>
            <Text size={200} style={{ color: "#63666A", display: "block" }}>Total Sessions</Text>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <Globe24Regular style={{ color: "#107C10" }} />
          </div>
          <div>
            <Text size={600} weight="semibold">{uniqueIps}</Text>
            <Text size={200} style={{ color: "#63666A", display: "block" }}>Unique IPs</Text>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}>
            <Timer24Regular style={{ color: "#D83B01" }} />
          </div>
          <div>
            <Text size={600} weight="semibold">{activeSessions}</Text>
            <Text size={200} style={{ color: "#63666A", display: "block" }}>Active Now</Text>
          </div>
        </Card>
      </div>

      {/* Audit Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>
            <Spinner size="medium" />
            <Text size={300} style={{ color: "#63666A" }}>Loading audit records...</Text>
          </div>
        ) : records.length === 0 ? (
          <div className={styles.emptyState}>
            <ShieldKeyhole24Regular style={{ fontSize: "48px", color: "#c4c7cc" }} />
            <Text size={400} weight="semibold" style={{ color: "#63666A" }}>
              No login records found
            </Text>
            <Text size={200} style={{ color: "#8a8d91" }}>
              Login events will appear here once the audit API is connected.
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Status</th>
                <th className={styles.th}>User</th>
                <th className={styles.th}>IP Address</th>
                <th className={styles.th}>Login Time</th>
                <th className={styles.th}>Last Seen</th>
                <th className={styles.th}>Duration</th>
                <th className={styles.th}>Browser</th>
              </tr>
            </thead>
            <tbody>
              {records.map((rec, idx) => (
                <tr key={rec.sessionId} className={idx % 2 === 0 ? undefined : styles.rowEven}>
                  <td className={styles.td}>
                    <Badge
                      appearance="filled"
                      color={rec.status === "active" ? "success" : "informative"}
                    >
                      {rec.status === "active" ? "Active" : "Ended"}
                    </Badge>
                  </td>
                  <td className={styles.td}>{rec.username}</td>
                  <td className={`${styles.td} ${styles.ipCell}`}>{rec.clientIp}</td>
                  <td className={`${styles.td} ${styles.timeCell}`}>
                    <Clock24Regular style={{ verticalAlign: "middle", marginRight: 4, fontSize: 14 }} />
                    {formatDateTime(rec.loginTime)}
                  </td>
                  <td className={`${styles.td} ${styles.timeCell}`}>{formatDateTime(rec.lastSeen)}</td>
                  <td className={styles.td}>{formatDuration(rec.durationSeconds)}</td>
                  <td className={`${styles.td} ${styles.uaCell}`} title={rec.userAgent}>
                    {parseUserAgent(rec.userAgent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};

export default AuditLogPage;
